// ClientHistory — merged Activity + Audit Log page with tabs.
// Replaces the separate Activity and Audit Log sidebar items.
import { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateTimeAU } from '@/lib/utils';
import { Activity, Clock, History, Shield, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import ClientHandoverLog from './ClientHandoverLog';

const ACTION_COLOURS = {
  create: 'bg-[#6B8E6B]/10 text-[#6B8E6B]',
  update: 'bg-[#4A3B32]/10 text-[#4A3B32]',
  verify: 'bg-[#6B8E6B]/10 text-[#6B8E6B]',
  flag: 'bg-[#D99A6C]/10 text-[#D99A6C]',
  status_change: 'bg-[#7D8C9E]/10 text-[#7D8C9E]',
  view: 'bg-[#F0EBE6] text-[#5D524D]',
};

export default function ClientHistory() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = ['audit', 'handover'].includes(params.get('tab')) ? params.get('tab') : 'activity';
  const [tab, setTabRaw] = useState(initial);
  const setTab = (id) => {
    setTabRaw(id);
    if (id === 'activity') params.delete('tab'); else params.set('tab', id);
    setParams(params, { replace: true });
  };
  const [activity, setActivity] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !clientId) return;
    setLoading(true);
    Promise.all([api.getActivity(token, clientId), api.getAudit(token, clientId)])
      .then(([a, au]) => { setActivity(a.data || []); setAudit(au.data || []); })
      .catch((e) => { console.error('ClientHistory load failed', e); })
      .finally(() => setLoading(false));
  }, [token, clientId]);

  return (
    <div className="animate-fade-in-up" data-testid="history-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl text-[#2C2420] flex items-center gap-2">
            <History className="w-5 h-5 text-[#A86A3A]" />
            History
          </h1>
          <p className="text-[#5D524D] text-sm">
            Recent activity and the complete audit trail for {client?.name}.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E6E0D9] mb-4" data-testid="history-tabs">
        {[
          { id: 'activity', label: `Activity (${activity.length})`, icon: Activity },
          { id: 'audit', label: `Audit Log (${audit.length})`, icon: Clock },
          { id: 'handover', label: 'Handover Log', icon: MessageSquare },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors',
                active
                  ? 'border-[#4A3B32] text-[#2C2420] font-medium'
                  : 'border-transparent text-[#5D524D] hover:text-[#2C2420]'
              )}
              data-testid={`history-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === 'handover' ? (
        <ClientHandoverLog />
      ) : tab === 'activity' ? (
        activity.length === 0 ? (
          <div className="empty-state">
            <Activity className="w-12 h-12" />
            <p className="text-lg font-medium text-[#5D524D]">No recent activity</p>
            <p className="text-sm">No activity recorded in the last 30 days</p>
          </div>
        ) : (
          <div className="caffeine-card">
            <div className="divide-y divide-[#E6E0D9]">
              {activity.map((item) => (
                <div key={item.id} className="p-4 flex items-start gap-4" data-testid={`activity-${item.id}`}>
                  <div className="w-10 h-10 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm font-medium flex-shrink-0">
                    {item.actor?.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#2C2420]">
                      <span className="font-medium">{item.actor}</span>{' '}
                      <span className="text-[#8C8078]">{item.action?.toLowerCase()}</span>{' '}
                      <span className="text-[#4A3B32] font-medium">{item.object}</span>
                    </p>
                    {item.outcome && <p className="text-sm text-[#5D524D] mt-1">{item.outcome}</p>}
                  </div>
                  <span className="text-xs text-[#8C8078] whitespace-nowrap">{formatDateTimeAU(item.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <>
          {client?.restricted && (
            <div className="bg-[#7D8C9E]/10 border border-[#7D8C9E]/20 rounded-lg p-4 mb-4 flex items-center gap-3">
              <Shield className="w-5 h-5 text-[#7D8C9E]" />
              <p className="text-sm text-[#5D524D]">All access to this restricted client is logged and audited.</p>
            </div>
          )}
          {audit.length === 0 ? (
            <div className="empty-state">
              <Clock className="w-12 h-12" />
              <p className="text-lg font-medium text-[#5D524D]">No audit events</p>
              <p className="text-sm">No audit events recorded for this client</p>
            </div>
          ) : (
            <div className="caffeine-card overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Object</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Outcome</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((event) => (
                    <tr key={event.id} data-testid={`audit-${event.id}`}>
                      <td className="font-mono text-sm">{formatDateTimeAU(event.timestamp)}</td>
                      <td className="font-medium text-[#2C2420]">{event.object}</td>
                      <td>{event.actor}</td>
                      <td>
                        <span className={cn('px-2 py-0.5 rounded text-xs', ACTION_COLOURS[event.action] || 'bg-[#F0EBE6] text-[#5D524D]')}>
                          {event.action?.replace('_', ' ')}
                        </span>
                      </td>
                      <td>{event.outcome}</td>
                      <td className="font-mono text-sm text-[#8C8078]">{event.ipAddress}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
